import React, { useEffect, useRef, useState } from "react";
import { Alert, Form, Input, InputNumber, DatePicker, Row, Col, Typography, Button, Descriptions, Divider, Modal, Select } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { format } from "date-fns";
import { getSessionUser } from "../../../../utils/auth/sessionUser";
import { Patient, PatientInfo } from "../../../../types/healthcare/PatientTypes";
import { validateMedicareNumber } from "../../../../utils/healthcare/ValidateMedicareNumber";
import { normaliseMedicareNumberForApi } from "../../../../utils/healthcare/NormaliseMedicareNumber";
import { validateIHI } from "../../../../utils/healthcare/ValidateIhiNumber";
import { formatIhiForPrint } from "../../../../utils/healthcare/FormatIhiForPrint";
import {
	DVA_FILE_NUMBER_TOO_LONG_MESSAGE,
	dvaFileNumberWithinLimit,
	toCanonicalDva,
} from "../../../../utils/healthcare/DvaFileNumber";
import { loadUserPreferences } from "../../../../utils/userPrefs/UserPrefs";
import { getFinanceCustomer, createFinanceCustomer, deleteFinanceCustomer } from "../../../../api/finance/FinanceApi";
import { fetchClinicCompany } from "../../../../api/admin/companyApi";
import { StripeCustomer } from "../../../../types/finance/FinanceTypes";
import { useNotifications } from "../../../../context/NotificationContext";
import PaymentMethodList from "../../../../components/finance/PaymentMethodList";
import AddPaymentMethodModal from "../../../../components/finance/AddPaymentMethodModal";
// I9: patient-scoped invoicing (Raise invoice + recent invoices). Self-contained and gated on
// canRaiseInvoices() internally, so it can be dropped without affecting the rest of this tab.
import PatientInvoicesSection from "./PatientInvoicesSection";
import SectionCard from "../../../../components/shared/SectionCard";
import InlineLoading from "../../../../components/shared/InlineLoading";
import EmptyState from "../../../../components/shared/EmptyState";
import HiServiceModeNotice from "../../../../components/healthcare/HiServiceModeNotice";
import dayjs from "dayjs";

const { Text } = Typography;

/** CU-14ygtkj8z2w: server-confirmed IHI trust state presented read-only; never the in-progress candidate. */
export type ServerConfirmedIhi = Pick<PatientInfo, "ihi_number" | "ihi_status" | "ihi_record_status" | "ihi_source">;

// CU-14ygtkj8z2w (HI_010_005848): every IHI starts "8003 6" plus type digit "0"; shown fixed, never editable.
const IHI_IDENTIFIER_TYPE = "IHI";
const IHI_FIXED_PREFIX = "8003 60";

interface BillingTabProps {
	updatedPatient: Patient;
	setUpdatedPatient: React.Dispatch<React.SetStateAction<Patient>>;
	handleAutoSave: () => void;
	onPatientUpdate?: () => void;
	/** B2.2F: parent signal that a server-evaluated manual-IHI outcome arrived. */
	ihiEvaluationSignal?: number;
	/** CU-14ygtkj8z2w: the host's last server-confirmed IHI snapshot, presented as Assigned / Current IHI. */
	serverConfirmedIhi?: ServerConfirmedIhi;
	/** CU-14ygtkj98p8: the server reported an IHI change the host could not yet re-read, so no IHI state is trustworthy. */
	ihiStateUnconfirmed?: boolean;
}

const BillingTab: React.FC<BillingTabProps> = ({ updatedPatient, setUpdatedPatient, handleAutoSave, onPatientUpdate, ihiEvaluationSignal, serverConfirmedIhi, ihiStateUnconfirmed = false }) => {
	const [medicareNumberError, setMedicareNumberError] = useState<string | null>(null);
	const [ihiNumberError, setIhiNumberError] = useState<string | null>(null);
	const [medicareInputValue, setMedicareInputValue] = useState<string>("");
	const [ihiInputValue, setIhiInputValue] = useState<string>("");
	const [dvaInputValue, setDvaInputValue] = useState<string>("");
	const [dvaFileNumberError, setDvaFileNumberError] = useState<string | null>(null);
	const [medicareValidTo, setMedicareValidTo] = useState<dayjs.Dayjs | null>(null);
	const [stripeCustomer, setStripeCustomer] = useState<StripeCustomer | null>(null);
	const [loadingStripeCustomer, setLoadingStripeCustomer] = useState<boolean>(false);
	const [companyId, setCompanyId] = useState<string>("");
	const [addPaymentMethodVisible, setAddPaymentMethodVisible] = useState<boolean>(false);
	const [refreshPaymentMethods, setRefreshPaymentMethods] = useState<number>(0);

	const { showError, showSuccess } = useNotifications();
	const user = getSessionUser();
	const { confirm } = Modal;

	useEffect(() => {
		if (updatedPatient.patient_info?.medicare_number) setMedicareInputValue(String(updatedPatient.patient_info.medicare_number));
		if (updatedPatient.patient_info?.ihi_number) setIhiInputValue(String(updatedPatient.patient_info.ihi_number));
		if (updatedPatient.patient_info?.medicare_valid_to) setMedicareValidTo(dayjs(updatedPatient.patient_info.medicare_valid_to));
		initializeStripeCustomer();
	}, []);

	// B2.2F (HI_010_005819): deterministic raw-field reconciliation. This runs ONLY
	// when the parent reports that a save round-trip returned a server-evaluated
	// manual-IHI outcome — never on ordinary patient prop changes, which would
	// overwrite in-progress operator input. At that point the parent state holds
	// the server-confirmed record, so the raw field re-displays the stored IHI
	// (verified candidate, or the preserved IHI after a rejected replacement) or
	// goes blank when the record has no IHI (discarded candidate). This removes a
	// rejected candidate from the field so it cannot be accidentally resubmitted,
	// while the B2.1A strict validation and blur-save gate stay unchanged.
	useEffect(() => {
		if (!ihiEvaluationSignal) return;
		const serverIhi = updatedPatient.patient_info?.ihi_number;
		setIhiInputValue(serverIhi === undefined || serverIhi === null ? "" : String(serverIhi));
		setIhiNumberError(null);
		// CU-14ygtkj98p8: leaving the unconfirmed state means the host has just re-read the patient on some refresh path.
	}, [ihiEvaluationSignal, ihiStateUnconfirmed]);

	// CU-14ygtkj8p14: follow a genuine record replacement (patient transition or refresh) without
	// overwriting raw in-progress text with the canonical value this control's own edit just wrote.
	const dvaSyncRef = useRef<{ patientId: Patient["id"] | undefined; canonical: string | null | undefined }>({
		patientId: undefined,
		canonical: undefined,
	});

	useEffect(() => {
		const stored = toCanonicalDva(updatedPatient.patient_info?.dva_file_number);
		if (dvaSyncRef.current.patientId === updatedPatient.id && dvaSyncRef.current.canonical === stored) return;
		dvaSyncRef.current = { patientId: updatedPatient.id, canonical: stored };
		setDvaInputValue(stored ?? "");
		setDvaFileNumberError(null);
	}, [updatedPatient]);

	const initializeStripeCustomer = async () => {
		try {
			const company = await fetchClinicCompany();
			setCompanyId(company.id.toString());
			if (updatedPatient.stripe_customer_id) await loadStripeCustomer(company.id.toString());
		} catch {}
	};

	const loadStripeCustomer = async (companyIdToUse: string) => {
		if (!updatedPatient.stripe_customer_id) return;
		setLoadingStripeCustomer(true);
		try {
			const customer = await getFinanceCustomer(updatedPatient.id);
			setStripeCustomer(customer.stripe_customer);
		} catch (error: any) {
			if (error.response?.status !== 404) showError("Failed to load Stripe customer information");
		} finally {
			setLoadingStripeCustomer(false);
		}
	};

	const handleCreateStripeCustomer = async () => {
		try {
			const customer = await createFinanceCustomer({
				userId: updatedPatient.id.toString(),
				email: updatedPatient.email,
				name: `${updatedPatient.first_name} ${updatedPatient.last_name}`,
				phone: updatedPatient.phone,
			});
			setUpdatedPatient((prev) => ({ ...prev, stripe_customer_id: customer.stripe_customer.id }));
			setStripeCustomer(customer.stripe_customer);
			showSuccess("Stripe customer created successfully");
			if (onPatientUpdate) onPatientUpdate();
		} catch {
			showError("Failed to create Stripe customer");
		}
	};

	const handleDeleteStripeCustomer = async () => {
		if (!stripeCustomer) return;
		confirm({
			title: "Delete Stripe Customer",
			content: "Are you sure you want to delete this Stripe customer? This action cannot be undone.",
			okText: "Delete",
			okType: "danger",
			onOk: async () => {
				try {
					await deleteFinanceCustomer(updatedPatient.id);
					setUpdatedPatient((prev) => ({ ...prev, stripe_customer_id: undefined }));
					setStripeCustomer(null);
					showSuccess("Stripe customer deleted");
					if (onPatientUpdate) onPatientUpdate();
				} catch {
					showError("Failed to delete Stripe customer");
				}
			},
		});
	};

	const handleMedicareChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		// Keep the exact raw text, for the same reason handleIhiChange does (PR #187) and for
		// the reason PR #240's review found here: a numeric parser strips grouping punctuation
		// before validation, so "2123,45670,1" would reach the validator as a checksum-valid
		// "2123456701", autosave, and never alert the operator — while letters parse to NaN and
		// fire no change event at all. Appendix D normalises display WHITESPACE only; every
		// other non-digit character must be rejected rather than repaired.
		const value = event.target.value;
		setMedicareInputValue(value);
		// Blank keeps the existing clearing semantics: no error, no state change — it must not
		// remove or overwrite a previously stored patient Medicare number.
		if (value === "") return;
		const validation = validateMedicareNumber(value);
		if (!validation.isValid) {
			setMedicareNumberError(validation.error || "Invalid Medicare number.");
			return;
		}
		setMedicareNumberError(null);
		// Only supported display whitespace is removed, and only after the raw entry passed
		// validation, so the stored value matches the continuous-digit API contract.
		setUpdatedPatient((prev) => ({
			...prev,
			patient_info: { ...prev.patient_info, medicare_number: Number(normaliseMedicareNumberForApi(value)) },
		}));
		handleAutoSave();
	};

	const handleIhiChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		// Keep the exact raw text: validateIHI must see the unparsed, unnormalised
		// entry so prohibited representations are rejected rather than repaired.
		const value = event.target.value;
		setIhiInputValue(value);
		// Blank keeps the existing clearing semantics: no error, no state change —
		// it must not remove or overwrite a previously stored patient IHI.
		if (value === "") return;
		const validation = validateIHI(value);
		if (!validation.isValid) {
			setIhiNumberError(validation.error || "Invalid IHI number.");
			return;
		}
		setIhiNumberError(null);
		// Only a validated continuous 16-digit string reaches the existing numeric contract.
		setUpdatedPatient((prev) => ({ ...prev, patient_info: { ...prev.patient_info, ihi_number: Number(value) } }));
	};

	const saveIfIhiValid = () => {
		// Gate autosave on the exact raw text currently in the field: invalid or
		// blank entries must never trigger a save, even when a previously stored
		// IHI is still valid.
		if (validateIHI(ihiInputValue).isValid) handleAutoSave();
	};

	const handleDvaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setDvaInputValue(value);
		if (!dvaFileNumberWithinLimit(value)) {
			setDvaFileNumberError(DVA_FILE_NUMBER_TOO_LONG_MESSAGE);
			return;
		}
		setDvaFileNumberError(null);
		const canonical = toCanonicalDva(value);
		dvaSyncRef.current = { patientId: updatedPatient.id, canonical };
		setUpdatedPatient((prev) => ({
			...prev,
			patient_info: { ...prev.patient_info, dva_file_number: canonical },
		}));
	};

	const saveIfDvaValid = () => {
		if (dvaFileNumberWithinLimit(dvaInputValue)) handleAutoSave();
	};

	const dateFormat = loadUserPreferences().dateFormat || "DD/MM/YYYY";
	const printedIhi = serverConfirmedIhi && !ihiStateUnconfirmed ? formatIhiForPrint(serverConfirmedIhi.ihi_number) : null;

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			{/* CU-14ygtkj65rw (HI_035_005845): the only printed IHI; sticky in print so it prints at any scroll position, while raw fields are no-print. */}
			{serverConfirmedIhi && printedIhi && (
				<div className="mg-ihi-print" data-testid="ihi-print-summary">
					<HiServiceModeNotice compact testId="ihi-print-mode-notice" />
					<Text strong>Assigned IHI</Text>
					<div data-testid="ihi-print-value">{printedIhi}</div>
					<Text type="secondary" style={{ fontSize: 12 }}>
						IHI Number Status: {serverConfirmedIhi.ihi_status ?? ""} · IHI Record Status: {serverConfirmedIhi.ihi_record_status ?? ""} · IHI Source: {serverConfirmedIhi.ihi_source ?? ""}
					</Text>
				</div>
			)}
			<PatientInvoicesSection patient={updatedPatient} />

			<SectionCard title="Stripe Payment Information">
				{loadingStripeCustomer ? (
					<InlineLoading message="Loading Stripe customer…" minHeight={120} />
				) : stripeCustomer ? (
					<div>
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
							<Text strong>Stripe Customer Details</Text>
							<Button danger size="small" icon={<DeleteOutlined />} onClick={handleDeleteStripeCustomer}>
								Delete Customer
							</Button>
						</div>
						<Descriptions bordered size="small" column={2}>
							<Descriptions.Item label="Customer ID" span={2}>{stripeCustomer.id}</Descriptions.Item>
							<Descriptions.Item label="Email" span={2}>{stripeCustomer.email}</Descriptions.Item>
							{stripeCustomer.name && <Descriptions.Item label="Name" span={2}>{stripeCustomer.name}</Descriptions.Item>}
							{stripeCustomer.phone && <Descriptions.Item label="Phone" span={2}>{stripeCustomer.phone}</Descriptions.Item>}
							<Descriptions.Item label="Created">
								{stripeCustomer.created ? format(new Date(stripeCustomer.created * 1000), "dd/MM/yyyy HH:mm") : "N/A"}
							</Descriptions.Item>
						</Descriptions>
						<Divider style={{ margin: "8px 0" }} />
						<PaymentMethodList
							customerId={stripeCustomer.id}
							onAddNew={() => setAddPaymentMethodVisible(true)}
							refreshTrigger={refreshPaymentMethods}
						/>
					</div>
				) : (
					<EmptyState
						title="No Stripe customer found for this patient"
						action={{ label: "Create Stripe Customer", onClick: handleCreateStripeCustomer }}
					/>
				)}
			</SectionCard>

			<SectionCard title="Medicare Card Information">
				<Form layout="vertical">
					<Row gutter={[16, 0]}>
						<Col xs={24} md={8}>
							<Form.Item label="Card Number" validateStatus={medicareNumberError ? "error" : ""} help={medicareNumberError}>
								<Input inputMode="numeric" value={medicareInputValue} onChange={handleMedicareChange} placeholder="Enter card number" />
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Reference Number">
								<InputNumber style={{ width: "100%" }} value={updatedPatient.patient_info?.medicare_individual_ref_number} onChange={(v) => setUpdatedPatient((prev) => ({ ...prev, patient_info: { ...prev.patient_info, medicare_individual_ref_number: v || undefined } }))} onBlur={handleAutoSave} placeholder="#" />
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Expiration Date">
								<DatePicker
									style={{ width: "100%" }}
									value={medicareValidTo}
									onChange={(date) => {
										setMedicareValidTo(date);
										setUpdatedPatient((prev) => ({ ...prev, patient_info: { ...prev.patient_info, medicare_valid_to: date ? date.toDate() : undefined } }));
									}}
									onBlur={handleAutoSave}
									format={dateFormat}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Medicare Gender Code">
								<Select
									style={{ width: "100%" }}
									allowClear
									placeholder="M / F"
									value={updatedPatient.patient_info?.medicare_gender_code || undefined}
									options={[{ label: "M", value: "M" }, { label: "F", value: "F" }]}
									onChange={(value) => {
										// CU-86d3ydtcj: antd clears with `undefined`, which JSON drops, so the
										// canonical `sometimes` rule would leave the stored code in place. An
										// explicit null is the accepted cleared value for this nullable field.
										setUpdatedPatient((prev) => ({ ...prev, patient_info: { ...prev.patient_info, medicare_gender_code: value ?? null } }));
										handleAutoSave();
									}}
								/>
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</SectionCard>

			<SectionCard title="DVA File Number Information">
				<Form layout="vertical">
					<Row gutter={[16, 0]}>
						<Col xs={24} md={8}>
							<Form.Item label="DVA File Number" validateStatus={dvaFileNumberError ? "error" : ""} help={dvaFileNumberError}>
								<Input value={dvaInputValue} onChange={handleDvaChange} onBlur={saveIfDvaValid} placeholder="Enter DVA file number" />
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</SectionCard>

			{/* HI_005_005802 / HI_010_005802 / HI_015_005802: manual IHI entry is
			    permitted wherever a patient demographic record is captured. This is the
			    UC.015 update surface; QuickCreatePatientModal is the UC.005 capture /
			    UC.010 registration surface. IHI Status and IHI Source are read-only —
			    they are server-owned trust state, stripped from every inbound write. */}
			<SectionCard title="IHI Number Information" className={printedIhi ? "no-print" : undefined}>
				<Form layout="vertical">
					{/* CU-14ygtkj8z2w: the B3.2 presentation needs the host's server-confirmed snapshot;
					    a host without one (ConsultContentTabs) keeps the previous layout unchanged. */}
					{serverConfirmedIhi && ihiStateUnconfirmed && (
						<Alert
							data-testid="ihi-state-unconfirmed-notice"
							type="warning"
							showIcon
							style={{ marginBottom: 16 }}
							message="IHI details need a refresh"
							description="The patient's IHI record changed and the current server state could not be confirmed. Refresh the patient record before viewing or editing IHI details."
						/>
					)}
					{serverConfirmedIhi && !ihiStateUnconfirmed && (
						<>
							{/* CU-14ygtkj8z2z (B3.4): HI Service mode from healthcare, shown with the server-confirmed presentation only. */}
							<HiServiceModeNotice compact />
							<div style={{ marginBottom: 8 }}>
								<Text strong>Assigned / Current IHI</Text>
								<br />
								<Text type="secondary" style={{ fontSize: 12 }}>Server-confirmed record. Read-only.</Text>
							</div>
							<Row gutter={[16, 0]}>
								<Col xs={24} md={8} className="no-print">
									<Form.Item label="Assigned IHI">
										<Input aria-label="Assigned IHI" value={serverConfirmedIhi.ihi_number == null ? "" : String(serverConfirmedIhi.ihi_number)} disabled />
									</Form.Item>
								</Col>
								<Col xs={24} md={8}>
									<Form.Item label="IHI Number Status">
										<Input aria-label="IHI Number Status" value={serverConfirmedIhi.ihi_status ?? ""} disabled />
									</Form.Item>
								</Col>
								<Col xs={24} md={8}>
									<Form.Item label="IHI Record Status">
										<Input aria-label="IHI Record Status" value={serverConfirmedIhi.ihi_record_status ?? ""} disabled />
									</Form.Item>
								</Col>
								<Col xs={24} md={8}>
									<Form.Item label="Identifier Type">
										<Input aria-label="Identifier Type" value={IHI_IDENTIFIER_TYPE} disabled />
									</Form.Item>
								</Col>
								<Col xs={24} md={8}>
									<Form.Item label="IHI Prefix">
										<Input aria-label="IHI Prefix" value={IHI_FIXED_PREFIX} disabled />
									</Form.Item>
								</Col>
								<Col xs={24} md={8}>
									<Form.Item label="IHI Source">
										<Input aria-label="IHI Source" value={serverConfirmedIhi.ihi_source ?? ""} disabled />
									</Form.Item>
								</Col>
							</Row>
							<Divider className="no-print" style={{ margin: "8px 0 16px" }} />
							<div className="no-print" style={{ marginBottom: 8 }}>
								<Text strong>Manual IHI Candidate</Text>
								<br />
								<Text type="secondary" style={{ fontSize: 12 }}>Editable. Validated on entry; the assigned IHI changes only after server confirmation.</Text>
							</div>
						</>
					)}
					<Row gutter={[16, 0]}>
						<Col xs={24} md={8} className="no-print">
							<Form.Item label="IHI Number" validateStatus={ihiNumberError ? "error" : ""} help={ihiNumberError}>
								{/* Text input, not InputNumber: the numeric parser would normalise
								    signed/decimal/exponent/grouped entries before validation, silently
								    repairing input the strict UC.005 / UC.010 / UC.015 contract requires
								    us to reject.
								    inputMode is a mobile-keyboard hint only — validateIHI stays the
								    validation authority. */}
								<Input inputMode="numeric" value={ihiStateUnconfirmed ? "" : ihiInputValue} onChange={handleIhiChange} onBlur={saveIfIhiValid} placeholder="Enter IHI number" disabled={ihiStateUnconfirmed} />
							</Form.Item>
						</Col>
						{!serverConfirmedIhi && (
							<>
								<Col xs={24} md={8}>
									<Form.Item label="IHI Status">
										<Input value={updatedPatient.patient_info?.ihi_status} disabled />
									</Form.Item>
								</Col>
								<Col xs={24} md={8}>
									<Form.Item label="IHI Source">
										<Input value={updatedPatient.patient_info?.ihi_source} disabled />
									</Form.Item>
								</Col>
							</>
						)}
					</Row>
				</Form>
			</SectionCard>

			{stripeCustomer && (
				<AddPaymentMethodModal
					visible={addPaymentMethodVisible}
					onClose={() => setAddPaymentMethodVisible(false)}
					onSuccess={() => setRefreshPaymentMethods((prev) => prev + 1)}
					customerId={stripeCustomer.id}
				/>
			)}
		</div>
	);
};

export default BillingTab;